import { queryOptions } from "@tanstack/react-query"
import { HttpTypes } from "@medusajs/types"
import { productKeys, rentalKeys, fullDetailsKeys, filterKeys } from "@lib/query-keys"
import { listProducts, listProductsWithSort } from "@lib/data/products"
import { getRentalInfo, checkRentalAvailability } from "@lib/data/rental"
import { getFilteredProductIds, type FilterResult } from "@lib/data/product-filters"
import { getFullProductDetails } from "@lib/data/product-details"
import { searchProducts } from "@modules/search/actions"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import type { RentalInfo, RentalAvailability } from "@/types/rental"
import type { FullProductDetails } from "@/types/full-details"

// ── Product List ──────────────────────────────────────────────────────────────

export interface ProductListFilters {
  sortBy?: SortOptions
  page?: number
  countryCode: string
  collectionId?: string
  categoryId?: string
  productsIds?: string[]
  limit?: number
}

export function productListQueryOptions(filters: ProductListFilters) {
  const {
    sortBy = "created_at",
    page = 1,
    countryCode,
    collectionId,
    categoryId,
    productsIds,
    limit = 12,
  } = filters

  const queryParams: Record<string, any> = { limit }

  if (collectionId) {
    queryParams.collection_id = [collectionId]
  }
  if (categoryId) {
    queryParams.category_id = [categoryId]
  }
  if (productsIds) {
    queryParams.id = productsIds
  }
  if (sortBy === "created_at") {
    queryParams.order = "created_at"
  }

  return queryOptions({
    queryKey: productKeys.list({
      sortBy,
      page,
      countryCode,
      collectionId,
      categoryId,
      productsIds,
      limit,
    }),
    queryFn: () =>
      listProductsWithSort({
        page,
        queryParams,
        sortBy,
        countryCode,
      }),
  })
}

// ── Product Detail ────────────────────────────────────────────────────────────

export function productDetailQueryOptions(handle: string, regionId: string) {
  return queryOptions({
    queryKey: productKeys.detail(handle),
    queryFn: async () => {
      const { response } = await listProducts({
        queryParams: { handle },
        regionId,
      })
      return response.products[0] as HttpTypes.StoreProduct | undefined
    },
  })
}

// ── Rental Info ───────────────────────────────────────────────────────────────

export function rentalInfoQueryOptions(
  productId: string,
  variantId?: string,
  currencyCode?: string
) {
  return queryOptions({
    queryKey: rentalKeys.info(productId),
    queryFn: () => getRentalInfo(productId, variantId, currencyCode),
  })
}

// ── Rental Availability ──────────────────────────────────────────────────────

export function rentalAvailabilityQueryOptions(
  productId: string,
  startDate: Date,
  endDate: Date,
  quantity: number = 1
) {
  return queryOptions({
    queryKey: rentalKeys.availability(
      productId,
      startDate.toISOString(),
      endDate.getTime() - startDate.getTime()
    ),
    queryFn: () =>
      checkRentalAvailability(productId, startDate, endDate, quantity),
  })
}

// ── Full Product Details (batch) ─────────────────────────────────────────────

export function fullDetailsQueryOptions(
  productId: string,
  locale: string,
  currencyCode?: string,
  variantId?: string
) {
  return queryOptions({
    queryKey: fullDetailsKeys.detail(productId),
    queryFn: () => getFullProductDetails(productId, locale, currencyCode, variantId),
  })
}

// ── Filter Facets ─────────────────────────────────────────────────────────────

/** Initial facets: type counts + content language + collections (no type filter, no hardcoded language list). */
export function filterInitialQueryOptions(
  locale: string,
  categoryId?: string,
  collectionId?: string,
  inStock?: boolean,
  contentLanguage?: string,
  languageIndependent?: string
) {
  // Backend's "all types" branch (handleCrossTypeFilter) cross-filters
  // on these dimensions, so the initial facets call needs them too —
  // otherwise toggling e.g. content_language while no product type is
  // selected leaves type_counts / language / collection counters stale.
  return queryOptions({
    queryKey: filterKeys.initial(
      [
        locale,
        categoryId ?? "",
        collectionId ?? "",
        inStock ? "1" : "0",
        contentLanguage ?? "",
        languageIndependent ?? "",
      ].join("|")
    ),
    queryFn: () =>
      getFilteredProductIds({
        locale,
        ...(categoryId ? { category_id: categoryId } : {}),
        ...(collectionId ? { collection_id: collectionId } : {}),
        ...(inStock ? { in_stock: "true" } : {}),
        ...(contentLanguage ? { content_language: contentLanguage } : {}),
        ...(languageIndependent ? { language_independent: languageIndependent } : {}),
      }),
    staleTime: 60_000,
  })
}

/** Type-specific facets with all active filters applied. */
export function filterFacetsQueryOptions(params: Record<string, string>) {
  return queryOptions({
    queryKey: filterKeys.facets(params),
    queryFn: () => getFilteredProductIds(params),
    enabled: !!params.type,
    staleTime: 30_000,
  })
}

// ── Search ────────────────────────────────────────────────────────────────────

export interface SearchFilters {
  query: string
  countryCode: string
  limit?: number
}

export function searchQueryOptions(filters: SearchFilters) {
  const { query, countryCode, limit = 24 } = filters

  return queryOptions({
    queryKey: productKeys.list({ search: query, countryCode, limit }),
    queryFn: () => searchProducts({ query, countryCode, limit }),
    enabled: query.length >= 2,
  })
}
